import { BooleanDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { DataFlowSink } from 'app/model/dataflow/dataflow-sink';
import { DataFlowSource } from 'app/model/dataflow/dataflow-source';
import { DataFlowExpression } from 'app/model/dataflow/dataflowexpression';
import { DatasetFormatType, DataFlowDatasetSettings } from 'app/model/dataflow/dataset-settings/dataflow-dataset-settings';
import { DelimitedTextSettings } from 'app/model/dataflow/dataset-settings/delimited-text-settings';
import { getAttributeValueFromDataset } from 'app/model/dataset/dataset.utils';
import {
    columnDelimiterOptions,
    escapeCharV2Options,
    quoteCharV2Options,
    rowDelimiterV2Options
} from 'app/shared/components/fileformat/fileformat.constants';
import { string2UnicodeSingle, unicode2StrSingle } from 'app/shared/utils/string.util';
import { Dataset, DataFlowDSLAdapterRegistry, LinkedService, Metamodel, Owned } from 'shell-models-lib';
import { DataFlowExpressionUtils } from '../expression.util';
import { DataFlowAdapterUtils } from '../adapter.util';
import { FileBasedSettingsAdapter } from './file-based-settings.adapter';

@DataFlowDSLAdapterRegistry.adapter({
    model: Metamodel.modelFor(DelimitedTextSettings)
})
export class DelimitedTextFormatAdapter extends FileBasedSettingsAdapter {
    /**
     * Supplements missing DSL properties for Fabric Migration.
     */
    public supplementPropsForFabricMigration(
        dataset: Dataset | undefined, linkedService: LinkedService | undefined, props: string[], isSource: boolean,
        isInline?: boolean, datasetSettings?: DataFlowDatasetSettings, _format?: string, wildcardPaths?: DataFlowExpression[]
    ): void {
        if (isInline || !dataset) {
            return;
        }

        props.push(`format: '${DatasetFormatType.Delimited}'`);

        DataFlowAdapterUtils.addCommonPropsForFileBasedFormats(dataset, linkedService, props, isSource, isInline, datasetSettings, wildcardPaths);

        const columnDelimiter = getAttributeValueFromDataset(dataset, 'columnDelimiter');
        if (columnDelimiter) {
            props.push(`columnDelimiter: ${DataFlowAdapterUtils.formatDelimiterValue(columnDelimiter)}`);
        }

        const rowDelimiter = getAttributeValueFromDataset(dataset, 'rowDelimiter');
        if (rowDelimiter) {
            props.push(`rowDelimiter: ${DataFlowAdapterUtils.formatDelimiterValue(rowDelimiter)}`);
        }

        const escapeChar = getAttributeValueFromDataset(dataset, 'escapeChar');
        if (escapeChar) {
            props.push(`escapeChar: ${DataFlowAdapterUtils.formatDelimiterValue(escapeChar)}`);
        }

        const quoteChar = getAttributeValueFromDataset(dataset, 'quoteChar');
        if (quoteChar) {
            props.push(`quoteChar: ${DataFlowAdapterUtils.formatDelimiterValue(quoteChar)}`);
        }

        const encodingName = getAttributeValueFromDataset(dataset, 'encodingName');
        if (encodingName) {
            props.push(`encodingName: ${DataFlowAdapterUtils.formatExpressionValue(encodingName)}`);
        }

        const nullValue = getAttributeValueFromDataset(dataset, 'nullValue');
        if (nullValue) {
            props.push(`nullValue: ${DataFlowAdapterUtils.formatExpressionValue(nullValue)}`);
        }

        const compressionCodec = getAttributeValueFromDataset(dataset, 'compressionCodec');
        if (compressionCodec) {
            props.push(`compressionCodec: '${compressionCodec}'`);
            const compressionLevel = getAttributeValueFromDataset(dataset, 'compressionLevel');
            if (compressionLevel) {
                props.push(`compressionLevel: ${DataFlowAdapterUtils.formatExpressionValue(compressionLevel)}`);
            }
        }

        const firstRowAsHeader = getAttributeValueFromDataset<boolean | string | null>(dataset, 'firstRowAsHeader');
        if (firstRowAsHeader !== null && firstRowAsHeader !== undefined) {
            const expr = new DataFlowExpression();
            expr.expression = String(firstRowAsHeader);
            props.push(`columnNamesAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(expr, BooleanDataFlowType.TYPE)}`);
        }
    }

    protected async toSourceDSL(delimitedTextSettings: DelimitedTextSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSourceDSL(delimitedTextSettings)));
        this._commonPropertiesToDSL(delimitedTextSettings, props);
        return props;
    }

    protected async toSinkDSL(delimitedTextSettings: DelimitedTextSettings): Promise<string[]> {
        const props = [];
        props.push(...(await super.toSinkDSL(delimitedTextSettings)));
        this._commonPropertiesToDSL(delimitedTextSettings, props);
        return props;
    }

    protected async toSourceModel(
        owner: DataFlowSource,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<DelimitedTextSettings> {
        const delimitedTextSettings = (await super.toSourceModel(
            owner,
            configs,
            handledConfigs
        )) as DelimitedTextSettings;
        this._commonPropertiesToModel(owner, delimitedTextSettings, configs, handledConfigs);
        return delimitedTextSettings;
    }

    protected async toSinkModel(
        owner: DataFlowSink,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): Promise<DelimitedTextSettings> {
        const delimitedTextSettings = (await super.toSinkModel(
            owner,
            configs,
            handledConfigs
        )) as DelimitedTextSettings;
        this._commonPropertiesToModel(owner, delimitedTextSettings, configs, handledConfigs);
        return delimitedTextSettings;
    }

    protected _generateSetting(owner: DataFlowSource | DataFlowSink): DelimitedTextSettings {
        return Owned.create<DelimitedTextSettings>(DelimitedTextSettings.MODEL, owner);
    }

    private _commonPropertiesToDSL(settings: DelimitedTextSettings, props: string[]): void {
        if (settings.compressionType) {
            props.push(`compressionCodec: '${settings.compressionType}'`);
            if (settings.compressionLevel.expression) {
                props.push(
                    `compressionLevel: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.compressionLevel)}`
                );
            }
        }
        if (settings.rowDelimiter.expression != null) {
            if (settings.rowDelimiter.templateExpression) {
                props.push(`rowDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.rowDelimiter)}`);
            } else {
                const transformedRowDelimiterExpression = new DataFlowExpression();
                transformedRowDelimiterExpression.expression = JSON.stringify(
                    string2UnicodeSingle(settings.rowDelimiter.expression)
                );
                transformedRowDelimiterExpression.expression = transformedRowDelimiterExpression.expression.substring(
                    1,
                    transformedRowDelimiterExpression.expression.length - 1
                );
                props.push(
                    `rowDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        transformedRowDelimiterExpression
                    )}`
                );
            }
        }
        if (settings.columnDelimiter.expression != null) {
            if (settings.columnDelimiter.templateExpression) {
                props.push(
                    `columnDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.columnDelimiter)}`
                );
            } else {
                const transformedColDelimiterExpression = new DataFlowExpression();
                transformedColDelimiterExpression.expression = JSON.stringify(
                    string2UnicodeSingle(settings.columnDelimiter.expression)
                );
                transformedColDelimiterExpression.expression = transformedColDelimiterExpression.expression.substring(
                    1,
                    transformedColDelimiterExpression.expression.length - 1
                );
                props.push(
                    `columnDelimiter: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                        transformedColDelimiterExpression
                    )}`
                );
            }
        }
        if (settings.escapeCharacter.expression != null) {
            if (settings.escapeCharacter.templateExpression) {
                props.push(
                    `escapeChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.escapeCharacter)}`
                );
            } else {
                const transformedEscapeCharExpression = new DataFlowExpression();
                transformedEscapeCharExpression.expression = JSON.stringify(
                    string2UnicodeSingle(settings.escapeCharacter.expression)
                );
                transformedEscapeCharExpression.expression = transformedEscapeCharExpression.expression.substring(
                    1,
                    transformedEscapeCharExpression.expression.length - 1
                );
                props.push(
                    `escapeChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(transformedEscapeCharExpression)}`
                );
            }
        }
        if (settings.quoteCharacter.expression != null) {
            if (settings.quoteCharacter.templateExpression) {
                props.push(`quoteChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.quoteCharacter)}`);
            } else {
                const transformedQuoteCharExpression = new DataFlowExpression();
                transformedQuoteCharExpression.expression = JSON.stringify(
                    string2UnicodeSingle(settings.quoteCharacter.expression)
                );
                transformedQuoteCharExpression.expression = transformedQuoteCharExpression.expression.substring(
                    1,
                    transformedQuoteCharExpression.expression.length - 1
                );
                props.push(
                    `quoteChar: ${DataFlowExpressionUtils.wrapExpressionConfigValue(transformedQuoteCharExpression)}`
                );
            }
        }
        if (settings.nullValue.expression) {
            props.push(`nullValue: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.nullValue)}`);
        }
        if (settings.encoding.expression) {
            props.push(`encodingName: ${DataFlowExpressionUtils.wrapExpressionConfigValue(settings.encoding)}`);
        }
        props.push(
            `columnNamesAsHeader: ${DataFlowExpressionUtils.wrapExpressionConfigValue(
                settings.columnNamesAsHeader,
                BooleanDataFlowType.TYPE
            )}`
        );
    }

    private _commonPropertiesToModel(
        owner: DataFlowSource | DataFlowSink,
        settings: DelimitedTextSettings,
        configs: Map<string, string>,
        handledConfigs: string[]
    ): void {
        if (configs.has('compressionCodec')) {
            settings.compressionType = DataFlowExpressionUtils.extractStringConfigValue(
                configs.get('compressionCodec')
            );
            handledConfigs.push('compressionCodec');
        }
        if (configs.has('compressionLevel')) {
            settings.compressionLevel = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('compressionLevel'),
                owner
            );
            handledConfigs.push('compressionLevel');
        }
        if (configs.has('rowDelimiter')) {
            settings.rowDelimiter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('rowDelimiter'),
                owner
            );
            if (!settings.rowDelimiter.templateExpression) {
                settings.rowDelimiter.expression = unicode2StrSingle(
                    JSON.parse(`"${settings.rowDelimiter.expression}"`),
                    rowDelimiterV2Options.map((option) => option.value)
                );
            }
            handledConfigs.push('rowDelimiter');
        }
        if (configs.has('columnDelimiter')) {
            settings.columnDelimiter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('columnDelimiter'),
                owner
            );
            if (!settings.columnDelimiter.templateExpression) {
                settings.columnDelimiter.expression = unicode2StrSingle(
                    JSON.parse(`"${settings.columnDelimiter.expression}"`),
                    columnDelimiterOptions.map((option) => option.value)
                );
            }
            handledConfigs.push('columnDelimiter');
        }
        if (configs.has('escapeChar')) {
            settings.escapeCharacter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('escapeChar'),
                owner
            );
            if (!settings.escapeCharacter.templateExpression) {
                settings.escapeCharacter.expression = unicode2StrSingle(
                    JSON.parse(`"${settings.escapeCharacter.expression}"`),
                    escapeCharV2Options.map((option) => option.value)
                );
            }
            handledConfigs.push('escapeChar');
        }
        if (configs.has('quoteChar')) {
            settings.quoteCharacter = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('quoteChar'),
                owner
            );
            if (!settings.quoteCharacter.templateExpression) {
                settings.quoteCharacter.expression = unicode2StrSingle(
                    JSON.parse(`"${settings.quoteCharacter.expression}"`),
                    quoteCharV2Options.map((option) => option.value)
                );
            }
            handledConfigs.push('quoteChar');
        }
        if (configs.has('nullValue')) {
            settings.nullValue = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('nullValue'), owner);
            handledConfigs.push('nullValue');
        }
        if (configs.has('encodingName')) {
            settings.encoding = DataFlowExpressionUtils.unwrapExpressionConfigValue(configs.get('encodingName'), owner);
            handledConfigs.push('encodingName');
        }
        if (configs.has('columnNamesAsHeader')) {
            settings.columnNamesAsHeader = DataFlowExpressionUtils.unwrapExpressionConfigValue(
                configs.get('columnNamesAsHeader'),
                owner,
                BooleanDataFlowType.TYPE
            );
            handledConfigs.push('columnNamesAsHeader');
        }
    }
}
